import type { ParameterGroup } from "../generated/api/types.gen";

export interface DisplayParameter {
  name: string;
  value: string;
  isAuto: boolean;
  /** Name of the asset this value came from, when picked from one. */
  assetName: string | null;
}

export interface DisplayParameterGroup {
  name: string;
  parameters: DisplayParameter[];
}

export function humanizeLabel(label: string): string {
  if (!label) {
    return "";
  }

  const withSpaces = label
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .trim();

  return withSpaces.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1),
  );
}

export function cleanGeneratedPrompt(
  prompt?: string,
  fallback?: string,
): string {
  if (!prompt?.trim()) {
    return fallback?.trim() || "No prompt available";
  }

  let cleaned = prompt;

  cleaned = cleaned.replace(
    /Parameter_groups:\s*\[[\s\S]*?\](?=(?:[,.;]|$))/gi,
    "",
  );

  cleaned = cleaned.replace(/\b([A-Za-z]+(?:_[A-Za-z]+)+)\b/g, (match) =>
    humanizeLabel(match),
  );

  cleaned = cleaned.replace(/\s{2,}/g, " ");

  cleaned = cleaned
    .replace(/\s+,/g, ", ")
    .replace(/\s+\./g, ". ")
    .replace(/\s+;/g, "; ")
    .replace(/\s+:/g, ": ");

  cleaned = cleaned
    .replace(/Image based on these specifications:\s*$/i, "")
    .trim();

  if (!cleaned) {
    return fallback?.trim() || "No prompt available";
  }

  return cleaned;
}

export function buildDisplayParameterGroups(
  parameterGroups?: ParameterGroup[] | null,
): DisplayParameterGroup[] {
  if (!parameterGroups || parameterGroups.length === 0) {
    return [];
  }

  return parameterGroups.map((group) => ({
    name: humanizeLabel(group.group_name),
    // Parameters are now a list of {key, value, is_prompt_param}
    // isAuto = true when is_prompt_param = false (auto-extracted by AI)
    parameters: (group.parameters || []).map((param) => ({
      name: humanizeLabel(param.key),
      value: param.value ?? "",
      isAuto: !param.is_prompt_param,
      assetName: param.asset_name ?? null,
    })),
  }));
}

export function formatParametersAsText(
  displayGroups: DisplayParameterGroup[],
): string {
  if (displayGroups.length === 0) {
    return "No parameters available";
  }

  return displayGroups
    .map((group) => {
      const params = group.parameters
        .map((p) => `- ${p.name}: ${p.value || "N/A"}`)
        .join("\n");
      return `${group.name}\n${params}`;
    })
    .join("\n\n");
}
